import { Text } from '@thanx/uikit/text'
import type { ConversationMessage, MessageRole } from 'api/recoveryAi.types'
import { buildTranslate } from 'locales'
import React, { useMemo } from 'react'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import { formatTime } from 'utilities/dateFormatters'

const t = buildTranslate('components.transcript')

type Props = {
  messages: ConversationMessage[]
}

// Known roles map to a friendly label; anything not in the MessageRole union
// would surface the raw role string as the fallback. Typed as
// Record<MessageRole, string> so adding a new value to the union without a
// corresponding label here is a compile error.
const ROLE_KEYS: Record<MessageRole, string> = {
  user: 'roles.user',
  ai: 'roles.ai',
  system: 'roles.system',
  tool_call: 'roles.tool_call',
}

const Transcript = ({ messages }: Props) => {
  const [css] = useStyletron()

  const ordered = useMemo(
    () => [...messages].sort((a, b) => a.sequence - b.sequence),
    [messages]
  )

  const bubbleClass = css({
    border: `1px solid ${merchantTheme.colors.grey20}`,
    borderRadius: '4px',
    padding: '16px',
    marginBottom: '12px',
    backgroundColor: 'white',
  })

  // Invariant header layout (role label left, timestamp right) — hoisted out of
  // the message map so it is built once per render rather than per message.
  const headerRowClass = css({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
  })

  return (
    <div>
      {ordered.map(message => {
        const roleKey = ROLE_KEYS[message.role]
        const roleLabel = roleKey ? t(roleKey) : message.role
        const timeLabel = formatTime(message.timestamp)
        return (
          <div key={message.sequence} className={bubbleClass}>
            <div className={headerRowClass}>
              <Text.BodyText5 color="grey70">{roleLabel}</Text.BodyText5>
              {timeLabel && (
                <Text.BodyText5 color="grey70">{timeLabel}</Text.BodyText5>
              )}
            </div>
            <div className={css({ marginTop: '4px' })}>
              <Text.BodyText4>{message.content}</Text.BodyText4>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default Transcript
